import { GraphQLClient, gql } from 'graphql-request';
import { invokeEdgeFunction } from './edgeFunctionUtils';

// Define and export response types
export interface DiffResponse {
  diff: {
    id: string;
    page_id: string;
    slug: string;
    v_before: string;
    v_after: string;
    asset_id?: string;
    hook?: string;
    description?: string;
    created_at?: string;
    updated_at?: string;
    changes: Array<{
      id: string;
      event_type?: string;
      slug: string;
      description?: string;
      before_extracts?: any;
      after_extract?: any;
      verified?: boolean;
      partially_correct?: boolean;
      invalid?: boolean;
      asset_id?: string;
      created_at?: string;
      updated_at?: string;
    }>;
    page?: {
      id: string;
      subslug?: string;
      pricing_page_url?: string;
      company?: {
        id: string;
        slug: string;
        url?: string;
        logo_url?: string;
      };
    };
  };
}

export interface EventResponse {
  event: {
    id: string;
    slug: string;
    event_type?: string;
    description?: string;
    before_extracts?: any;
    after_extract?: any;
    verified?: boolean;
    partially_correct?: boolean;
    invalid?: boolean;
    asset_id?: string;
    created_at?: string;
    updated_at?: string;
    v_before?: string;
    v_after?: string;
    page_id?: string;
  };
}

export interface CompanyResponse {
  company: {
    id: string;
    slug: string;
    employees?: string | number | null;
    category?: string;
    logo_url?: string;
    description?: string;
    url?: string;
    scraper?: string;
    pages?: Array<{
      id: string;
      subslug: string | null;
      pricing_page_url?: string;
      plans?: Array<{
        id: string;
        display_name?: string;
        add_on?: boolean;
        plan_name?: string;
      }> | null;
    }> | null;
  };
}

interface CompaniesResponse {
  companies: Array<{
    id: string;
    slug: string;
  }>;
}

import { getSupabaseUrl, getApiKey } from './supabaseConfig';
import { supabase } from '@/integrations/supabase/client';

// Create the GraphQL endpoint URL based on the Supabase URL
const endpoint = `${getSupabaseUrl()}/functions/v1/graphql`;

// Function to get headers with proper authentication
const getHeaders = async () => {
  const apiKey = getApiKey();
  const session = await supabase.auth.getSession();

  console.log('🔐 API Key length:', apiKey ? apiKey.length : 'undefined');
  console.log('🔐 Session data exists:', !!session.data);
  console.log('🔐 Access token length:', session.data.session?.access_token ? session.data.session.access_token.length : 'undefined');

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'apikey': apiKey,
  };

  // If user is authenticated, use their token; otherwise use anon key
  if (session.data.session?.access_token) {
    const token = session.data.session.access_token;
    console.log('🔐 Using access token (first 20 chars):', token.substring(0, 20) + '...');
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    console.log('🔐 Using API key as fallback (first 20 chars):', apiKey.substring(0, 20) + '...');
    headers['Authorization'] = `Bearer ${apiKey}`;
  }

  return headers;
};

// Create the GraphQL client with basic headers, we'll update them dynamically
export const graphqlClient = new GraphQLClient(endpoint, {
  headers: {
    'Content-Type': 'application/json',
    'apikey': getApiKey(),
    'Authorization': `Bearer ${getApiKey()}`,
  },
  credentials: 'same-origin',
});

// Function to update GraphQL client with current session
export const updateGraphQLClientAuth = async () => {
  const headers = await getHeaders();
  graphqlClient.setHeaders(headers);
};

// Reduced logging for cleaner console output

// Add global test function for browser console
declare global {
  interface Window {
    testGraphQL?: (slug: string) => Promise<any>;
  }
}

// Add a connectivity check function to test the endpoint directly
const testGraphQLConnectivity = async () => {
  try {
    const apiKey = getApiKey();
    const response = await fetch(`${getSupabaseUrl()}/functions/v1/graphql`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': apiKey,
        'Authorization': `Bearer ${apiKey}`, // Include Authorization header in Bearer format
      },
      body: JSON.stringify({
        query: `{ __schema { queryType { name } } }` // Simple introspection query
      })
    });
    
    
    if (!response.ok) {
      console.error('❌ [GraphQL] Connection test failed with status:', response.status);
      const text = await response.text();
      console.error('❌ [GraphQL] Error response:', text);
      return false;
    }
    
    const data = await response.json();
    return true;
  } catch (error) {
    console.error('❌ [GraphQL] Connection test error:', error);
    return false;
  }
};

// Disabled automatic connectivity test to reduce console noise
// setTimeout(() => {
//   testGraphQLConnectivity();
// }, 2000);

// Set auth token for authenticated requests
export const setAuthToken = (token: string | null) => {
  if (token) {
    graphqlClient.setHeader('Authorization', `Bearer ${token}`);
  } else {
    graphqlClient.setHeaders({});
  }
};

// Test query with more fields for debugging GraphQL issues
const TEST_GRAPHQL_QUERY = `
  query TestCompanyQuery($slug: String!) {
    company(slug: $slug) {
      id
      slug
      # Fields that might cause issues
      employees
      category
      logo_url
      description
      url
      scraper
      # Try including pages to see if it works
      pages {
        id
        subslug
        pricing_page_url
        # Include plans for each page
        plans {
          id
          display_name
          add_on
          plan_name
        }
      }
    }
  }
`;

// Define and export the company query
export const COMPANY_QUERY = `
  query GetCompany($slug: String!) {
    company(slug: $slug) {
      id
      slug
      employees
      category
      logo_url
      description
      url
      scraper
      pages {
        id
        subslug
        pricing_page_url
        # Include plans for each page
        plans {
          id
          display_name
          add_on
          plan_name
        }
      }
    }
  }
`;

/**
 * Test the GraphQL endpoint with a comprehensive query
 * This function is for debugging only and won't affect the main application flow
 */
export const testGraphQLEndpoint = async (slug: string): Promise<any> => {
  try {
    console.log(`🔬 [GraphQL-Test] Testing GraphQL endpoint with slug: ${slug}`);
    
    // Use fetch directly to get the full response for debugging
    const response = await fetch(`${getSupabaseUrl()}/functions/v1/graphql`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': getApiKey(),
        'Authorization': `Bearer ${getApiKey()}`,
      },
      body: JSON.stringify({
        query: TEST_GRAPHQL_QUERY,
        variables: { slug }
      })
    });
    
    console.log(`🔬 [GraphQL-Test] Response status:`, response.status);
    
    // Parse the response
    const result = await response.json();
    
    // Log detailed information about the response
    console.log(`🔬 [GraphQL-Test] GraphQL response:`, result);
    
    if (result.errors) {
      console.error(`🔬 [GraphQL-Test] GraphQL errors:`, result.errors);
      
      // Parse and log specific field errors
      result.errors.forEach((err: any, index: number) => {
        console.error(`🔬 [GraphQL-Test] Error ${index + 1}:`, {
          message: err.message,
          path: err.path,
          locations: err.locations
        });
      });
    }
    
    if (result.data?.company) {
      // Log specific fields that might cause issues
      console.log(`🔬 [GraphQL-Test] Company data fields:`, {
        id: result.data.company.id,
        slug: result.data.company.slug,
        employees: {
          value: result.data.company.employees,
          type: typeof result.data.company.employees
        },
        hasPages: Array.isArray(result.data.company.pages),
        pagesCount: Array.isArray(result.data.company.pages) ? result.data.company.pages.length : 0
      });
    }
    
    return result;
  } catch (error) {
    console.error(`❌ [GraphQL-Test] Error testing GraphQL:`, error);
    throw error;
  }
}

// Define versions query with all related data
const VERSIONS_QUERY = `
  query GetVersions($companySlug: String!) {
    versions(companySlug: $companySlug) {
      id
      company_id
      version_number
      created_at
      notes
      plans {
        id
        name
        interval
        charges {
          id
          name
          type
          min_price
          max_price
          price
          unit
        }
        discounts {
          id
          type
          amount_type
          name
          description
          amount
        }
      }
    }
  }
`;

// Define events query
const EVENTS_QUERY = `
  query GetEvents($companySlug: String!) {
    events(companySlug: $companySlug) {
      id
      company_id
      event_date
      event_type
      event_name
      event_description
      source_url
      created_at
    }
  }
`;

// Define quarterly events query
const QUARTERLY_EVENTS_QUERY = `
  query GetQuarterlyEvents($companySlug: String!) {
    quarterlyEvents(companySlug: $companySlug) {
      id
      company_id
      event_date
      quarter
      year
      event_type
      event_name
      event_description
      source_url
      created_at
    }
  }
`;

// Lightweight navigation query - no heavy extract data
const DIFFS_NAVIGATION_QUERY = `
  query GetDiffsNavigation($limit: Int) {
    diffsNavigation(limit: $limit) {
      id
      slug
      v_before
      v_after
      created_at
      changesSummary {
        id
        event_type
        verified
        partially_correct
        invalid
      }
    }
  }
`;

// Define diffs query
const DIFFS_QUERY = `
  query GetDiffs($slug: String, $page_id: String, $v_before: String, $v_after: String, $quarter: String, $event_type: String, $verification_status: String, $created_after: String, $published: Boolean, $periods: [String], $includeChanges: Boolean, $limit: Int, $offset: Int) {
    diffs(slug: $slug, page_id: $page_id, v_before: $v_before, v_after: $v_after, quarter: $quarter, event_type: $event_type, verification_status: $verification_status, created_after: $created_after, published: $published, periods: $periods, includeChanges: $includeChanges, limit: $limit, offset: $offset) {
      diffs {
        id
        page_id
        slug
        v_before
        v_after
        asset_id
        hook
        description
        published
        created_at
        updated_at
        period
        delta
        primary_cropped_url
        secondary_cropped_url
        primary_original_url
        secondary_original_url
        changes {
          id
          event_type
          slug
          v_before
          v_after
          description
          before_extracts
          after_extract
          verified
          partially_correct
          invalid
          asset_id
          asset {
            id
            primary_original_url
            primary_cropped_url
            primary_markup_url
            secondary_original_url
            secondary_cropped_url
            secondary_markup_url
          }
          created_at
          updated_at
        }
        changesSummary {
          id
          event_type
          verified
          partially_correct
          invalid
        }
        page {
          id
          subslug
          pricing_page_url
          company {
            id
            slug
            url
            logo_url
          }
        }
      }
      totalCount
      hasNextPage
      hasPrevPage
      currentPage
      pageSize
      publishedCount
    }
  }
`;

// Define diffs statistics query
const DIFFS_STATISTICS_QUERY = `
  query GetDiffsStatistics($slug: String, $page_id: String, $v_before: String, $v_after: String, $quarter: String, $event_type: String, $verification_status: String, $created_after: String, $published: Boolean, $periods: [String]) {
    diffsStatistics(slug: $slug, page_id: $page_id, v_before: $v_before, v_after: $v_after, quarter: $quarter, event_type: $event_type, verification_status: $verification_status, created_after: $created_after, published: $published, periods: $periods) {
      totalDiffs
      uniqueCompanies
      totalChanges
      verified
      partiallyCorrect
      unverified
      published
      unpublished
      eventTypes {
        price
        plan
        feature
        discount
        metric
        other
      }
    }
  }
`;

// Define news query with pagination
const NEWS_QUERY = `
  query GetNews($quarter: String, $slug: String, $published: Boolean, $verified: Boolean, $priceChange: Boolean, $planChange: Boolean, $metricChange: Boolean, $discountChange: Boolean, $featuresChange: Boolean, $limit: Int, $offset: Int) {
    news(quarter: $quarter, slug: $slug, published: $published, verified: $verified, priceChange: $priceChange, planChange: $planChange, metricChange: $metricChange, discountChange: $discountChange, featuresChange: $featuresChange, limit: $limit, offset: $offset) {
      news {
        id
        slug
        page_id
        version
        before_version
        quarter
        hook
        description
        before_image_url
        after_image_url
        gif_url
        published
        verified
        created_at
        zoom_level
        price_change
        plan_change
        metric_change
        discount_change
        features_change
        annotations
        asset_id
        assets {
          id
          collection_id
          type
          page_id
          slug
          version
          title
          description
          primary_image
          secondary_image
          primary_original_url
          primary_cropped_url
          primary_markup_url
          secondary_original_url
          secondary_cropped_url
          secondary_markup_url
          crop_aspect_ratio
          crop_coordinates
          primary_crop_coordinates
          primary_markup_coordinates
          secondary_crop_coordinates
          secondary_markup_coordinates
          header
          subheader
          comparison_mode
          secondary_version
          secondary_page_id
          primary_annotations
          secondary_annotations
          primary_viewport_state
          secondary_viewport_state
          created_at
          updated_at
          company_slug
          logo_url
          page {
            id
            pricing_page_url
          }
        }
      }
      totalCount
      hasNextPage
      hasPrevPage
      currentPage
      pageSize
      publishedCount
      verifiedCount
    }
  }
`;

// Define maps query
const MAPS_QUERY = `
  query GetMaps($companySlug: String!) {
    maps(companySlug: $companySlug) {
      id
      company_id
      title
      description
      image_url
      created_at
    }
  }
`;

// The original versions query that's not working yet
// This is kept for reference for when the backend issues are fixed
const ORIGINAL_VERSIONS_QUERY = `
  query GetVersions($companySlug: String!) {
    versions(companySlug: $companySlug) {
      id
      company_id
      version_number
      created_at
      notes
      plans {
        id
        name
        interval
        charges {
          id
          name
          type
          min_price
          max_price
          price
          unit
        }
        discounts {
          id
          type
          amount_type
          name
          description
          amount
        }
      }
    }
  }
`;

// Define plans query with charges and discounts
const PLANS_QUERY = `
  query GetPlans($companySlug: String!, $pageSubslug: String, $versionNumber: Int) {
    plans(companySlug: $companySlug, pageSubslug: $pageSubslug, versionNumber: $versionNumber) {
      id
      name
      interval
      company_id
      page_id
      version
      charges {
        id
        name
        plan_id
        type
        min_price
        max_price
        price
        unit
        metrics {
          id
          slug
          display_name
          modality
        }
      }
      discounts {
        id
        type
        amount_type
        name
        plan_id
        description
        amount
        metrics {
          id
          slug
          display_name
          modality
        }
      }
    }
  }
`;

// Define the companies query with filtering
const COMPANIES_QUERY = `
  query GetCompanies($filter: CompanyFilterInput) {
    companies(filter: $filter) {
      id
      slug
      logoUrl
      employees
      category
      description
      url
    }
  }
`;

// Additional type definitions for advanced GraphQL responses - used internally
interface ExtendedCompanyResponse {
  company: {
    id: string;
    slug: string;
    logo_url?: string; // Using consistent naming with snake_case to match our schema
    employees?: string;
    category?: string;
    description?: string;
    url?: string;
    scraper?: string;
    pages?: Array<{
      id: string;
      subslug: string;
      pricing_page_url?: string; // Using consistent naming with snake_case to match our schema
      screenshot_url?: string;
      screenshot_url_version?: string;
      plans?: Array<any>;
      pricing_metrics?: Array<any>;
      tags?: Array<any>;
    }>;
    pricing_metrics?: Array<{
      id: string;
      slug: string;
      display_name: string;
      modality?: string;
      subcategory?: string;
      sample_values?: string[];
      sample_prices?: string[];
    }>;
    tags?: Array<{
      id: string;
      name: string;
      slug: string;
    }>;
    tasks?: Array<{
      id: string;
      type: string;
      status: string;
      created_at: string;
      parameters?: string;
    }>;
    events?: Array<{
      id: string;
      company_id: string;
      event_date?: string;
      event_type?: string;
      event_name?: string;
      event_description?: string;
      source_url?: string;
      created_at: string;
    }>;
    quarterlyEvents?: Array<{
      id: string;
      company_id: string;
      event_date?: string;
      quarter?: string;
      year?: number;
      event_type?: string;
      event_name?: string;
      event_description?: string;
      source_url?: string;
      created_at: string;
    }>;
  };
}

interface VersionsResponse {
  versions: Array<{
    id: string;
    company_id: string;
    version_number: number;
    created_at: string;
    notes?: string;
    plans?: Array<{
      id: string;
      name?: string;
      interval?: string;
      charges?: Array<any>;
      discounts?: Array<any>;
    }>;
  }>;
}

interface EventsResponse {
  events: Array<{
    id: string;
    company_id: string;
    event_date?: string;
    event_type?: string;
    event_name?: string;
    event_description?: string;
    source_url?: string;
    created_at: string;
  }>;
}

interface QuarterlyEventsResponse {
  quarterlyEvents: Array<{
    id: string;
    company_id: string;
    event_date?: string;
    quarter?: string;
    year?: number;
    event_type?: string;
    event_name?: string;
    event_description?: string;
    source_url?: string;
    created_at: string;
  }>;
}

interface MapsResponse {
  maps: Array<{
    id: string;
    company_id: string;
    title?: string;
    description?: string;
    image_url?: string;
    created_at: string;
  }>;
}

interface DiffsResponse {
  diffs: {
    diffs: Array<{
      id: string;
      page_id: string;
      slug: string;
      v_before: string;
      v_after: string;
      asset_id?: string;
      hook?: string;
      description?: string;
      created_at?: string;
      updated_at?: string;
      changes: Array<{
        id: string;
        event_type?: string;
        slug: string;
        description?: string;
        before_extracts?: any;
        after_extract?: any;
        verified?: boolean;
        partially_correct?: boolean;
        invalid?: boolean;
        asset_id?: string;
        created_at?: string;
        updated_at?: string;
      }>;
      page?: {
        id: string;
        subslug?: string;
        pricing_page_url?: string;
        company?: {
          id: string;
          slug: string;
          url?: string;
          logo_url?: string;
        };
      };
    }>;
    totalCount: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    currentPage: number;
    pageSize: number;
    publishedCount: number;
  };
}

interface NewsResponse {
  news: {
    news: Array<{
      id: string;
      slug: string;
      page_id: string;
      version: string;
      before_version?: string;
      quarter?: string;
      hook?: string;
      description?: string;
      before_image_url?: string;
      after_image_url?: string;
      gif_url?: string;
      published: boolean;
      verified?: boolean;
      created_at?: string;
      zoom_level?: number;
      annotations?: any;
      asset_id?: string;
      price_change?: boolean;
      plan_change?: boolean;
      metric_change?: boolean;
      discount_change?: boolean;
      features_change?: boolean;
      assets?: Array<{
        id: string;
        collection_id: string;
        type: string;
        page_id: string;
        slug: string;
        version: string;
        title: string;
        description?: string;
        primary_image?: string;
        secondary_image?: string;
        primary_original_url?: string;
        primary_cropped_url?: string;
        primary_markup_url?: string;
        secondary_original_url?: string;
        secondary_cropped_url?: string;
        secondary_markup_url?: string;
        crop_aspect_ratio?: string;
        crop_coordinates?: string;
        header?: string;
        subheader?: string;
        comparison_mode?: string;
        secondary_version?: string;
        secondary_page_id?: string;
        primary_annotations?: string;
        secondary_annotations?: string;
        primary_viewport_state?: any;
        secondary_viewport_state?: any;
        created_at: string;
        updated_at: string;
        company_slug?: string;
        logo_url?: string;
        page?: {
          id: string;
          pricing_page_url?: string;
        };
      }>;
    }>;
    totalCount: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    currentPage: number;
    pageSize: number;
    publishedCount: number;
    verifiedCount: number;
  };
}

// Fetcher functions that use the GraphQL client

// Function to fetch a company with all related data
export async function fetchCompanyWithDetails(slug: string): Promise<CompanyResponse> {
  try {
    console.log(`🔍 [Edge Function] Fetching company details for slug: ${slug}`);
    
    // Use the edge function directly as the primary source
    const data = await invokeEdgeFunction('get-company-details', { slug });
    console.log(`✅ [Edge Function] Successfully fetched company data for ${slug}`);
    
    if (data?.company) {
      console.log(`📊 [Edge Function] Company data fields:`, Object.keys(data.company));
      console.log(`📊 [Edge Function] Pages:`, data.company.pages?.length || 0);
    }
    
    return data;
  } catch (error) {
    console.error(`❌ [Edge Function] Error fetching company data:`, error);
    
    // Fall back to basic GraphQL query as last resort
    try {
      console.log(`🔄 [Edge Function] Falling back to GraphQL for basic company data`);
      const data = await graphqlClient.request<CompanyResponse>(COMPANY_QUERY, { slug });
      return data;
    } catch (graphqlError) {
      console.error(`❌ [GraphQL] Fallback also failed:`, graphqlError);
      throw error; // Rethrow the original error
    }
  }
}

// Function to fetch versions for a company
export async function fetchVersions(companySlug: string): Promise<VersionsResponse> {
  try {
    
    // Use the full versions query now that the resolver is fixed
    const data = await graphqlClient.request<VersionsResponse>(VERSIONS_QUERY, { companySlug });
    
    // Add detailed debugging for data structure
    
    // Check if we got versions and if they have nested plans
    const hasPlans = data.versions?.some(v => v.plans && v.plans.length > 0);
    const plansCount = data.versions?.reduce((count, version) => count + (version.plans?.length || 0), 0) || 0;
    
    console.log(`[rcheckpoint1] GraphQL Versions data:`, {
      hasVersions: !!data.versions && data.versions.length > 0,
      count: data.versions?.length || 0,
      versionNumbers: data.versions?.map(v => v.version_number) || [],
      hasPlans,
      plansCount
    });
    
    // If we have versions but no plans, we might need to fetch plans separately
    if (data.versions?.length > 0 && !hasPlans) {
      console.log(`[rcheckpoint1] GraphQL versions missing plans data, will try separate fetching for each version`);
      
      // Process each version to fetch plans if needed
      // This is a fallback to handle cases where the resolver isn't returning nested plans
      const processedVersions = [...data.versions];
      
      return { versions: processedVersions };
    }
    
    // Return the actual versions data
    return data;
    
  } catch (error) {
    console.error(`❌ [GraphQL] Error fetching versions:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    // Fall back to edge function
    return invokeEdgeFunction('get-versions', { slug: companySlug });
  }
}

// Function to fetch plans with charges and discounts
export async function fetchPlans(companySlug: string, pageSubslug?: string, versionNumber?: number) {
  try {
    const data = await graphqlClient.request(PLANS_QUERY, { 
      companySlug, 
      pageSubslug: pageSubslug || undefined, 
      versionNumber: versionNumber || undefined 
    });
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error fetching plans:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    // Fall back to edge function
    return invokeEdgeFunction('get-plans', { 
      slug: companySlug, 
      subslug: pageSubslug,
      version: versionNumber 
    });
  }
}

// Function to fetch companies with filtering
export async function fetchCompanies(filter?: string): Promise<CompaniesResponse> {
  try {
    const data = await graphqlClient.request<CompaniesResponse>(COMPANIES_QUERY, { filter });
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error fetching companies list:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    // Fall back to edge function
    return invokeEdgeFunction('get-companies', { filter });
  }
}

// Function to fetch diffs for navigation (lightweight)
export async function fetchDiffsNavigation(
  limit?: number
): Promise<any> {
  try {
    await updateGraphQLClientAuth();
    const data = await graphqlClient.request<any>(DIFFS_NAVIGATION_QUERY, {
      limit
    });
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error fetching diffs navigation:`, error);
    throw error;
  }
}

// Function to fetch diffs
export async function fetchDiffs(
  slug?: string,
  page_id?: string,
  v_before?: string,
  v_after?: string,
  quarter?: string,
  event_type?: string,
  verification_status?: string,
  created_after?: string,
  published?: boolean,
  periods?: string[],
  limit?: number,
  offset?: number,
  includeChanges?: boolean
): Promise<DiffsResponse> {
  try {
    await updateGraphQLClientAuth(); // Ensure we have the latest auth token
    const data = await graphqlClient.request<DiffsResponse>(DIFFS_QUERY, {
      slug,
      page_id,
      v_before,
      v_after,
      quarter,
      event_type,
      verification_status,
      created_after,
      published,
      periods,
      includeChanges,
      limit,
      offset
    });

    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error fetching diffs:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    throw error;
  }
}

// Function to fetch ALL diffs by paginating through all pages
export async function fetchAllDiffs(
  slug?: string,
  page_id?: string,
  v_before?: string,
  v_after?: string,
  quarter?: string,
  event_type?: string,
  verification_status?: string,
  created_after?: string,
  published?: boolean,
  periods?: string[],
  includeChanges?: boolean
): Promise<DiffsResponse> {
  console.log('[fetchAllDiffs] Starting with params:', { slug, periods, created_after, published });

  const pageSize = 100; // Use larger page size for efficiency
  let offset = 0;
  let allDiffs: any[] = [];
  let hasMore = true;
  let totalCount = 0;
  let publishedCount = 0;

  try {
    while (hasMore) {
      const response = await fetchDiffs(
        slug,
        page_id,
        v_before,
        v_after,
        quarter,
        event_type,
        verification_status,
        created_after,
        published,
        periods,
        pageSize,
        offset,
        includeChanges
      );

      allDiffs = allDiffs.concat(response.diffs.diffs);
      totalCount = response.diffs.totalCount;
      publishedCount = response.diffs.publishedCount;
      hasMore = response.diffs.hasNextPage;
      offset += pageSize;

      console.log(`[fetchAllDiffs] Fetched ${allDiffs.length}/${totalCount} diffs (hasMore: ${hasMore})`);
    }

    return {
      diffs: {
        diffs: allDiffs,
        totalCount,
        publishedCount,
        hasNextPage: false,
        hasPrevPage: false,
        currentPage: 1,
        pageSize: allDiffs.length
      }
    };
  } catch (error) {
    console.error(`❌ [GraphQL] Error fetching all diffs:`, error);
    throw error;
  }
}

// Function to fetch news items with pagination and change type filters
export async function fetchNews(
  quarter?: string,
  slug?: string,
  published?: boolean | null,
  verified?: boolean | null,
  priceChange?: boolean | null,
  planChange?: boolean | null,
  metricChange?: boolean | null,
  discountChange?: boolean | null,
  featuresChange?: boolean | null,
  limit: number = 50,
  offset: number = 0
): Promise<NewsResponse> {
  try {
    await updateGraphQLClientAuth(); // Ensure we have the latest auth token
    const data = await graphqlClient.request<NewsResponse>(NEWS_QUERY, {
      quarter,
      slug,
      published,
      verified,
      priceChange: priceChange === true ? true : undefined,
      planChange: planChange === true ? true : undefined,
      metricChange: metricChange === true ? true : undefined,
      discountChange: discountChange === true ? true : undefined,
      featuresChange: featuresChange === true ? true : undefined,
      limit,
      offset
    });

    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error fetching news:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    throw error;
  }
}

// Define update news mutation
const UPDATE_NEWS_MUTATION = `
  mutation UpdateNews($id: ID!, $input: NewsUpdateInput!) {
    updateNews(id: $id, input: $input) {
      id
      slug
      page_id
      version
      before_version
      quarter
      hook
      description
      before_image_url
      after_image_url
      gif_url
      published
      verified
      created_at
      zoom_level
      price_change
      plan_change
      metric_change
      discount_change
      features_change
      annotations
      asset_id
      assets {
        id
        collection_id
        type
        page_id
        slug
        version
        title
        description
        primary_image
        secondary_image
        primary_original_url
        primary_cropped_url
        primary_markup_url
        secondary_original_url
        secondary_cropped_url
        secondary_markup_url
        crop_aspect_ratio
        crop_coordinates
        primary_crop_coordinates
        primary_markup_coordinates
        secondary_crop_coordinates
        secondary_markup_coordinates
        header
        subheader
        comparison_mode
        secondary_version
        secondary_page_id
        primary_annotations
        secondary_annotations
        created_at
        updated_at
        company_slug
        logo_url
      }
    }
  }
`;

// Define delete news mutation
const DELETE_NEWS_MUTATION = `
  mutation DeleteNews($id: ID!) {
    deleteNews(id: $id) {
      id
      success
      message
    }
  }
`;

// Define toggle news published mutation
const TOGGLE_NEWS_PUBLISHED_MUTATION = `
  mutation ToggleNewsPublished($id: ID!) {
    toggleNewsPublished(id: $id) {
      id
      slug
      published
      verified
      hook
      description
    }
  }
`;

// Define toggle diff published mutation
const TOGGLE_DIFF_PUBLISHED_MUTATION = `
  mutation ToggleDiffPublished($id: ID!) {
    toggleDiffPublished(id: $id) {
      id
      slug
      published
      hook
      description
    }
  }
`;

// Define event verification mutation
const UPDATE_EVENT_VERIFIED_MUTATION = `
  mutation UpdateEventVerified($eventId: ID!, $verified: Boolean!) {
    updateEventVerified(eventId: $eventId, verified: $verified) {
      id
      event_type
      slug
      description
      before_extracts
      after_extract
      verified
      partially_correct
      invalid
      asset_id
      created_at
      updated_at
    }
  }
`;

// Define event partially correct mutation
const UPDATE_EVENT_PARTIALLY_CORRECT_MUTATION = `
  mutation UpdateEventPartiallyCorrect($eventId: ID!, $partiallyCorrect: Boolean!) {
    updateEventPartiallyCorrect(eventId: $eventId, partiallyCorrect: $partiallyCorrect) {
      id
      event_type
      slug
      description
      before_extracts
      after_extract
      verified
      partially_correct
      invalid
      asset_id
      created_at
      updated_at
    }
  }
`;

// Define event invalid mutation
const UPDATE_EVENT_INVALID_MUTATION = `
  mutation UpdateEventInvalid($eventId: ID!, $invalid: Boolean!) {
    updateEventInvalid(eventId: $eventId, invalid: $invalid) {
      id
      event_type
      slug
      description
      before_extracts
      after_extract
      verified
      partially_correct
      invalid
      asset_id
      created_at
      updated_at
    }
  }
`;

// Define event type update mutation
const UPDATE_EVENT_TYPE_MUTATION = `
  mutation UpdateEventType($eventId: ID!, $eventType: EventType!) {
    updateEventType(eventId: $eventId, eventType: $eventType) {
      id
      event_type
      slug
      description
      before_extracts
      after_extract
      verified
      partially_correct
      invalid
      asset_id
      created_at
      updated_at
    }
  }
`;

// Define event description update mutation
const UPDATE_EVENT_DESCRIPTION_MUTATION = `
  mutation UpdateEventDescription($eventId: ID!, $description: String!) {
    updateEventDescription(eventId: $eventId, description: $description) {
      id
      event_type
      slug
      description
      before_extracts
      after_extract
      verified
      partially_correct
      invalid
      asset_id
      created_at
      updated_at
    }
  }
`;

// Define merge events mutation
const MERGE_EVENTS_MUTATION = `
  mutation MergeEvents($sourceEventId: ID!, $targetEventId: ID!) {
    mergeEvents(sourceEventId: $sourceEventId, targetEventId: $targetEventId) {
      id
      event_type
      slug
      description
      before_extracts
      after_extract
      verified
      partially_correct
      invalid
      asset_id
      created_at
      updated_at
    }
  }
`;

// Define delete event mutation
const DELETE_EVENT_MUTATION = `
  mutation DeleteEvent($eventId: ID!) {
    deleteEvent(eventId: $eventId) {
      id
      success
      message
    }
  }
`;

// Define clone event mutation
const CLONE_EVENT_MUTATION = `
  mutation CloneEvent($eventId: ID!) {
    cloneEvent(eventId: $eventId) {
      id
      event_type
      slug
      description
      before_extracts
      after_extract
      verified
      partially_correct
      invalid
      asset_id
      created_at
      updated_at
    }
  }
`;

// Define delete diff mutation
const DELETE_DIFF_MUTATION = `
  mutation DeleteDiff($id: ID!) {
    deleteDiff(id: $id) {
      id
      success
      message
    }
  }
`;

// Define single diff query
const DIFF_QUERY = `
  query GetDiff($id: ID!) {
    diff(id: $id) {
      id
      page_id
      slug
      v_before
      v_after
      asset_id
      hook
      description
      published
      created_at
      updated_at
      changes {
        id
        event_type
        slug
        description
        before_extracts
        after_extract
        verified
        partially_correct
        invalid
        asset_id
        created_at
        updated_at
      }
      page {
        id
        subslug
        pricing_page_url
        company {
          id
          slug
          url
          logo_url
        }
      }
    }
  }
`;

// Define single event query
const EVENT_QUERY = `
  query GetEvent($id: ID!) {
    event(id: $id) {
      id
      slug
      event_type
      description
      before_extracts
      after_extract
      verified
      partially_correct
      invalid
      asset_id
      created_at
      updated_at
      v_before
      v_after
      page_id
    }
  }
`;

// Define events by IDs query (bulk fetch)
const EVENTS_BY_IDS_QUERY = `
  query GetEventsByIds($ids: [ID!]!) {
    eventsByIds(ids: $ids) {
      id
      diff_id
      event_type
      slug
      description
      v_before
      v_after
      before_extracts
      after_extract
      verified
      partially_correct
      invalid
      asset_id
      created_at
      updated_at
    }
  }
`;

// Define types for the annotation system
interface AnnotationRectangleInput {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  label?: string;
  created_at: string;
}

interface ImageAnnotationInput {
  image_type: string;
  zoom_level: number;
  pan_x: number;
  pan_y: number;
  rectangles: AnnotationRectangleInput[];
}

interface NewsUpdateInput {
  hook?: string;
  description?: string;
  zoom_level?: number;
  annotations?: any;
  price_change?: boolean;
  plan_change?: boolean;
  metric_change?: boolean;
  discount_change?: boolean;
  features_change?: boolean;
  published?: boolean;
  verified?: boolean;
  before_image_url?: string;
  after_image_url?: string;
}

// Asset mutation types
interface AssetInput {
  collection_id: string;
  type: string;
  page_id: string;
  slug: string;
  version: string;
  title: string;
  description?: string;
  primary_original_url?: string;
  primary_cropped_url?: string;
  primary_markup_url?: string;
  secondary_original_url?: string;
  secondary_cropped_url?: string;
  secondary_markup_url?: string;
  primary_image?: string;
  secondary_image?: string;
  header?: string;
  subheader?: string;
  comparison_mode?: string;
  secondary_version?: string;
  secondary_page_id?: string;
  primary_annotations?: string;
  secondary_annotations?: string;
  crop_coordinates?: string;
  primary_crop_coordinates?: any;
  primary_markup_coordinates?: any;
  secondary_crop_coordinates?: any;
  secondary_markup_coordinates?: any;
}

interface AssetUpdateInput {
  collection_id?: string;
  type?: string;
  page_id?: string;
  slug?: string;
  version?: string;
  title?: string;
  description?: string;
  primary_original_url?: string;
  primary_cropped_url?: string;
  primary_markup_url?: string;
  secondary_original_url?: string;
  secondary_cropped_url?: string;
  secondary_markup_url?: string;
  secondary_version?: string;
  primary_image?: string;
  secondary_image?: string;
  crop_aspect_ratio?: string;
  crop_coordinates?: string;
  header?: string;
  subheader?: string;
  comparison_mode?: string;
  secondary_page_id?: string;
  primary_annotations?: string;
  secondary_annotations?: string;
  primary_crop_coordinates?: any;
  primary_markup_coordinates?: any;
  secondary_crop_coordinates?: any;
  secondary_markup_coordinates?: any;
}

// GraphQL mutations for assets
const CREATE_ASSET_MUTATION = gql`
  mutation CreateAsset($input: AssetInput!) {
    createAsset(input: $input) {
      id
      collection_id
      type
      page_id
      slug
      version
      title
      description
      primary_original_url
      primary_cropped_url
      primary_markup_url
      secondary_original_url
      secondary_cropped_url
      secondary_markup_url
      crop_aspect_ratio
      crop_coordinates
      primary_crop_coordinates
      primary_markup_coordinates
      secondary_crop_coordinates
      secondary_markup_coordinates
      header
      subheader
      comparison_mode
      secondary_version
      secondary_page_id
      primary_annotations
      secondary_annotations
      created_at
      updated_at
      company_slug
      logo_url
    }
  }
`;

const UPDATE_ASSET_MUTATION = gql`
  mutation UpdateAsset($id: ID!, $input: AssetUpdateInput!) {
    updateAsset(id: $id, input: $input) {
      id
      collection_id
      type
      page_id
      slug
      version
      primary_original_url
      primary_cropped_url
      secondary_original_url
      secondary_cropped_url
      header
      subheader
      comparison_mode
      secondary_version
      primary_viewport_state
      secondary_viewport_state
      created_at
      updated_at
      company_slug
      logo_url
    }
  }
`;

// Function to create an asset
export async function createAsset(input: AssetInput) {
  try {
    await updateGraphQLClientAuth();
    const data = await graphqlClient.request(CREATE_ASSET_MUTATION, { input });
    return data;
  } catch (error) {
    console.error('Error creating asset:', error);
    throw error;
  }
}

// Function to update an asset
export async function updateAsset(id: string, input: AssetUpdateInput) {
  try {
    // Update GraphQL client auth before making the request
    await updateGraphQLClientAuth();

    const data = await graphqlClient.request(UPDATE_ASSET_MUTATION, { id, input });
    return data;
  } catch (error) {
    console.error('Error updating asset:', error);
    throw error;
  }
}

// Function to update a news item
export async function updateNews(id: string, input: NewsUpdateInput) {
  try {
    await updateGraphQLClientAuth(); // Ensure we have the latest auth token
    const data = await graphqlClient.request(UPDATE_NEWS_MUTATION, { id, input });
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error updating news:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    throw error;
  }
}

// Function to toggle news published status
export async function toggleNewsPublished(id: string) {
  try {
    await updateGraphQLClientAuth(); // Ensure we have the latest auth token
    const data = await graphqlClient.request(TOGGLE_NEWS_PUBLISHED_MUTATION, { id });
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error toggling news published:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    throw error;
  }
}

// Function to toggle diff published status
export async function toggleDiffPublished(id: string) {
  try {
    await updateGraphQLClientAuth(); // Ensure we have the latest auth token
    const data = await graphqlClient.request(TOGGLE_DIFF_PUBLISHED_MUTATION, { id });
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error toggling diff published:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    throw error;
  }
}

// Function to delete a news item
export async function deleteNews(id: string) {
  try {
    await updateGraphQLClientAuth(); // Ensure we have the latest auth token
    const data = await graphqlClient.request(DELETE_NEWS_MUTATION, { id });
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error deleting news:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    throw error;
  }
}

// Function to fetch a single diff
export async function fetchDiff(id: string): Promise<DiffResponse> {
  try {
    await updateGraphQLClientAuth(); // Ensure we have the latest auth token
    const data = await graphqlClient.request<DiffResponse>(DIFF_QUERY, { id });
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error fetching diff:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    throw error;
  }
}

// Function to fetch the most recent diff by slug
export async function fetchDiffBySlug(slug: string): Promise<DiffResponse> {
  try {
    await updateGraphQLClientAuth(); // Ensure we have the latest auth token
    const data = await graphqlClient.request<DiffsResponse>(DIFFS_QUERY, {
      slug,
      limit: 1,
      includeChanges: true
    });

    if (!data.diffs?.diffs || data.diffs.diffs.length === 0) {
      throw new Error(`No diffs found for slug: ${slug}`);
    }

    // Return the first diff in the same format as fetchDiff
    return { diff: data.diffs.diffs[0] };
  } catch (error) {
    console.error(`❌ [GraphQL] Error fetching diff by slug:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    throw error;
  }
}

// Function to fetch a single event
export async function fetchEvent(id: string): Promise<EventResponse> {
  try {
    await updateGraphQLClientAuth(); // Ensure we have the latest auth token
    const data = await graphqlClient.request<EventResponse>(EVENT_QUERY, { id });
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error fetching event:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    throw error;
  }
}

// Function to fetch events by diff IDs (bulk fetch)
export async function fetchEventsByDiffIds(diffIds: string[]): Promise<any[]> {
  try {
    await updateGraphQLClientAuth();
    const data = await graphqlClient.request<{ eventsByIds: any[] }>(EVENTS_BY_IDS_QUERY, { ids: diffIds });
    return data.eventsByIds || [];
  } catch (error) {
    console.error(`❌ [GraphQL] Error fetching events by IDs:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    throw error;
  }
}

// Function to update event verification status
export async function updateEventVerified(eventId: string, verified: boolean) {
  try {
    await updateGraphQLClientAuth(); // Ensure we have the latest auth token
    const data = await graphqlClient.request(UPDATE_EVENT_VERIFIED_MUTATION, { eventId, verified });
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error updating event verification:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    throw error;
  }
}

// Function to update event partially correct status
export async function updateEventPartiallyCorrect(eventId: string, partiallyCorrect: boolean) {
  try {
    await updateGraphQLClientAuth(); // Ensure we have the latest auth token
    const data = await graphqlClient.request(UPDATE_EVENT_PARTIALLY_CORRECT_MUTATION, { eventId, partiallyCorrect });
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error updating event partially correct status:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    throw error;
  }
}

// Function to update event invalid status
export async function updateEventInvalid(eventId: string, invalid: boolean) {
  try {
    await updateGraphQLClientAuth(); // Ensure we have the latest auth token
    const data = await graphqlClient.request(UPDATE_EVENT_INVALID_MUTATION, { eventId, invalid });
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error updating event invalid status:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    throw error;
  }
}

// Function to update event type
export async function updateEventType(eventId: string, eventType: 'price' | 'plan' | 'feature' | 'discount' | 'metric' | 'other') {
  try {
    await updateGraphQLClientAuth(); // Ensure we have the latest auth token
    const data = await graphqlClient.request(UPDATE_EVENT_TYPE_MUTATION, { eventId, eventType });
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error updating event type:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    throw error;
  }
}

// Function to update event description
export async function updateEventDescription(eventId: string, description: string) {
  try {
    await updateGraphQLClientAuth(); // Ensure we have the latest auth token
    const data = await graphqlClient.request(UPDATE_EVENT_DESCRIPTION_MUTATION, { eventId, description });
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error updating event description:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    throw error;
  }
}

// Function to merge two events
export async function mergeEvents(sourceEventId: string, targetEventId: string) {
  try {
    await updateGraphQLClientAuth(); // Ensure we have the latest auth token
    const data = await graphqlClient.request(MERGE_EVENTS_MUTATION, { sourceEventId, targetEventId });
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error merging events:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    throw error;
  }
}

// Function to delete an event
export async function deleteEvent(eventId: string) {
  try {
    await updateGraphQLClientAuth(); // Ensure we have the latest auth token
    const data = await graphqlClient.request(DELETE_EVENT_MUTATION, { eventId });
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error deleting event:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    throw error;
  }
}

// Function to clone an event
export async function cloneEvent(eventId: string) {
  try {
    await updateGraphQLClientAuth(); // Ensure we have the latest auth token
    const data = await graphqlClient.request(CLONE_EVENT_MUTATION, { eventId });
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error cloning event:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    throw error;
  }
}

// Function to delete a diff
export async function deleteDiff(id: string) {
  try {
    await updateGraphQLClientAuth(); // Ensure we have the latest auth token
    const data = await graphqlClient.request(DELETE_DIFF_MUTATION, { id });
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error deleting diff:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    throw error;
  }
}

// GraphQL mutation for updating diff fields
const UPDATE_DIFF_MUTATION = `
  mutation UpdateDiff($id: ID!, $hook: String, $description: String) {
    updateDiff(id: $id, hook: $hook, description: $description) {
      id
      hook
      description
      updated_at
    }
  }
`;

// Function to update diff fields (hook and/or description)
export async function updateDiff(id: string, fields: { hook?: string; description?: string }) {
  try {
    await updateGraphQLClientAuth();
    const data = await graphqlClient.request(UPDATE_DIFF_MUTATION, { id, ...fields });
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error updating diff:`, error);
    throw error;
  }
}

// Function to fetch diffs statistics
export async function fetchDiffsStatistics(
  slug?: string,
  page_id?: string,
  v_before?: string,
  v_after?: string,
  quarter?: string,
  event_type?: string,
  verification_status?: string,
  created_after?: string,
  published?: boolean,
  periods?: string[]
) {
  try {
    await updateGraphQLClientAuth(); // Ensure we have the latest auth token
    const variables = {
      slug: slug || undefined,
      page_id: page_id || undefined,
      v_before: v_before || undefined,
      v_after: v_after || undefined,
      quarter: quarter || undefined,
      event_type: event_type || undefined,
      verification_status: verification_status || undefined,
      created_after: created_after || undefined,
      published: published,
      periods: periods || undefined
    };

    const data = await graphqlClient.request(DIFFS_STATISTICS_QUERY, variables);
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error fetching diffs statistics:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    throw error;
  }
}

// ============================================================================
// Claude Tasks
// ============================================================================

export interface ClaudeTasksResponse {
  claudeTasks: {
    tasks: Array<{
      id: string;
      command: string;
      params: Record<string, any>;
      status: string;
      result: Record<string, any> | null;
      created_at: string;
      updated_at: string;
      slug: string;
    }>;
    totalCount: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface ClaudeTasksStatisticsResponse {
  claudeTasksStatistics: {
    totalTasks: number;
    pending: number;
    running: number;
    completed: number;
    failed: number;
    byCommand: {
      run_diff: number;
      scrape: number;
      verify: number;
      run_extract: number;
    };
  };
}

export interface ClaudeTaskResponse {
  claudeTask: {
    id: string;
    command: string;
    params: Record<string, any>;
    status: string;
    result: Record<string, any> | null;
    created_at: string;
    updated_at: string;
    slug: string;
  };
}

const CLAUDE_TASKS_QUERY = `
  query GetClaudeTasks($slug: String, $command: ClaudeTaskCommand, $status: [ClaudeTaskStatus], $created_after: String, $periods: [String], $limit: Int, $offset: Int) {
    claudeTasks(slug: $slug, command: $command, status: $status, created_after: $created_after, periods: $periods, limit: $limit, offset: $offset) {
      tasks {
        id
        command
        params
        status
        result
        created_at
        updated_at
        slug
      }
      totalCount
      hasNextPage
      hasPrevPage
    }
  }
`;

const CLAUDE_TASKS_STATISTICS_QUERY = `
  query GetClaudeTasksStatistics($slug: String, $command: ClaudeTaskCommand, $status: [ClaudeTaskStatus], $created_after: String, $periods: [String]) {
    claudeTasksStatistics(slug: $slug, command: $command, status: $status, created_after: $created_after, periods: $periods) {
      totalTasks
      pending
      running
      completed
      failed
      byCommand {
        run_diff
        scrape
        verify
      }
    }
  }
`;

const AVAILABLE_PERIODS_QUERY = `
  query GetAvailablePeriods {
    availablePeriods
  }
`;

const CLAUDE_TASK_QUERY = `
  query GetClaudeTask($id: ID!) {
    claudeTask(id: $id) {
      id
      command
      params
      status
      result
      created_at
      updated_at
      slug
    }
  }
`;

export async function fetchClaudeTasks(
  slug?: string,
  command?: string,
  status?: string[],
  created_after?: string,
  periods?: string[],
  limit?: number,
  offset?: number
): Promise<ClaudeTasksResponse> {
  try {
    await updateGraphQLClientAuth();
    const data = await graphqlClient.request<ClaudeTasksResponse>(CLAUDE_TASKS_QUERY, {
      slug,
      command,
      status,
      created_after,
      periods,
      limit,
      offset
    });
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error fetching claude tasks:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response errors:`, error.response.errors);
    }
    throw error;
  }
}

export async function fetchClaudeTasksStatistics(
  slug?: string,
  command?: string,
  status?: string[],
  created_after?: string,
  periods?: string[]
): Promise<ClaudeTasksStatisticsResponse> {
  try {
    await updateGraphQLClientAuth();
    const data = await graphqlClient.request<ClaudeTasksStatisticsResponse>(
      CLAUDE_TASKS_STATISTICS_QUERY,
      {
        slug,
        command,
        status,
        created_after,
        periods
      }
    );
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error fetching claude tasks statistics:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    throw error;
  }
}

export interface AvailablePeriodsResponse {
  availablePeriods: string[];
}

export async function fetchAvailablePeriods(): Promise<AvailablePeriodsResponse> {
  try {
    await updateGraphQLClientAuth();
    const data = await graphqlClient.request<AvailablePeriodsResponse>(
      AVAILABLE_PERIODS_QUERY
    );
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error fetching available periods:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response errors:`, error.response.errors);
    }
    throw error;
  }
}

export async function fetchClaudeTask(id: string): Promise<ClaudeTaskResponse> {
  try {
    await updateGraphQLClientAuth();
    const data = await graphqlClient.request<ClaudeTaskResponse>(CLAUDE_TASK_QUERY, { id });
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error fetching claude task:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    throw error;
  }
}

// ============================================================================
// DIFF REPORTS
// ============================================================================

const DIFF_REPORTS_QUERY = gql`
  query DiffReports($period: String, $limit: Int, $offset: Int) {
    diffReports(period: $period, limit: $limit, offset: $offset) {
      id
      period
      created_at
      updated_at
      companies_count
      diffs_count
      trends_count
      events_count
      markdown_url
      json_url
      html_url
      analysis_metadata
      period_start
      period_end
      published
      published_url
      published_at
    }
  }
`;

const DIFF_REPORT_QUERY = gql`
  query DiffReport($id: ID!) {
    diffReport(id: $id) {
      id
      period
      created_at
      updated_at
      companies_count
      diffs_count
      trends_count
      events_count
      markdown_url
      json_url
      html_url
      analysis_metadata
      period_start
      period_end
      draft_url
      published
      published_url
      published_at
    }
  }
`;

interface DiffReportsResponse {
  diffReports: Array<{
    id: string;
    period: string;
    created_at: string;
    updated_at: string;
    companies_count?: number;
    diffs_count?: number;
    trends_count?: number;
    events_count?: number;
    markdown_url?: string;
    json_url?: string;
    html_url?: string;
    analysis_metadata?: any;
    period_start?: string;
    period_end?: string;
    published?: boolean;
    published_url?: string;
    published_at?: string;
  }>;
}

interface DiffReportResponse {
  diffReport: {
    id: string;
    period: string;
    created_at: string;
    updated_at: string;
    companies_count?: number;
    diffs_count?: number;
    trends_count?: number;
    events_count?: number;
    markdown_url?: string;
    json_url?: string;
    html_url?: string;
    analysis_metadata?: {
      insights?: Array<{
        title: string;
        description: string;
      }>;
    };
    period_start?: string;
    period_end?: string;
    draft_url?: string;
    published?: boolean;
    published_url?: string;
    published_at?: string;
  };
}

export async function fetchDiffReports(
  period?: string,
  limit: number = 50,
  offset: number = 0
): Promise<DiffReportsResponse> {
  try {
    await updateGraphQLClientAuth();
    const data = await graphqlClient.request<DiffReportsResponse>(
      DIFF_REPORTS_QUERY,
      { period, limit, offset }
    );
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error fetching diff reports:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    throw error;
  }
}

export async function fetchDiffReport(id: string): Promise<DiffReportResponse> {
  try {
    await updateGraphQLClientAuth();
    const data = await graphqlClient.request<DiffReportResponse>(DIFF_REPORT_QUERY, { id });
    return data;
  } catch (error) {
    console.error(`❌ [GraphQL] Error fetching diff report:`, error);
    if (error.response) {
      console.error(`❌ [GraphQL] Response status:`, error.response.status);
      console.error(`❌ [GraphQL] Response data:`, error.response.data);
    }
    throw error;
  }
}

// ============================================================================

// Expose the test function globally for browser console testing
// This won't affect normal application flow
if (typeof window !== 'undefined') {
  window.testGraphQL = async (slug: string) => {
    console.log(`🧪 [Test] Running GraphQL test with slug: ${slug}`);
    try {
      const result = await testGraphQLEndpoint(slug);
      console.log(`✅ [Test] Test completed successfully`);
      return result;
    } catch (error) {
      console.error(`❌ [Test] Test failed:`, error);
      return error;
    }
  };

}
